import { DatePicker } from "@material-ui/pickers";
import React, { useCallback, useState } from "react";
import {
  AutocompleteInput,
  Button,
  ButtonProps,
  maxLength,
  ReferenceInput,
  required,
  SimpleForm,
  TextInput,
  Toolbar,
  ToolbarProps,
  useAuthenticated,
  useNotify,
} from "react-admin";
import resourceNames from "../../../common/data/resourceNames";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import GetAppIcon from "@material-ui/icons/GetApp";
import SaveIcon from "@material-ui/icons/Save";
import { useForm } from "react-final-form";
import { CircularProgress, makeStyles } from "@material-ui/core";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { FmcsaTransferApiFactory } from "../../../common/data/service-proxies";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { RaDriverPicker } from "../../common/form/RaDriverPicker";
import { handleAxiosError } from "../../../common/data/axiosErrorHandler";

interface Props {}

const ToolbarCustomButton = ({
  handleSubmit,
  handleSubmitWithRedirect,
  invalid,
  pristine,
  saving,
  submitOnEnter,
  ...rest
}: ToolbarProps & ButtonProps) => (
  <Button size="medium" variant="contained" {...rest}>
    {rest.children}
  </Button>
);

const useStyles = makeStyles(
  (theme) => ({
    leftIcon: {
      marginRight: theme.spacing(1),
    },
  }),
  { name: "RaSaveButton" }
);

const FormToolbar = (props: any) => {
  const { invalid } = props;
  const form = useForm();
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(0);
  const axios = useAxiosInstance();
  const notify = useNotify();
  const classes = useStyles();

  const submitToFmcsa = useCallback(() => {
    if (loading || !axios) {
      return;
    }

    setLoading(true);
    setLoader(0);
    const formState = form.getState();
    const data = formState.values;
    FmcsaTransferApiFactory(undefined, axios?.defaults.baseURL, axios)
      .apiAppFmcsatransferSubmittowebservicePost({
        driverId: data.driverId,
        vehicleId: data.vehicleId,
        dateFrom: data.dateFrom,
        dateTo: data.dateTo,
        countryCode: "US",
        comment: data.comment,
      })
      .then(() =>
        notify("resources.fmcsa-data-transfer.messages.fileSubmitted")
      )
      .catch((e) => {
        console.error(e);
        notify(handleAxiosError(e), { type: "error" });
      })
      .finally(() => setLoading(false));
  }, [form, axios, loading, notify]);

  const downloadEldFile = useCallback(() => {
    if (loading || !axios) {
      return;
    }

    setLoading(true);
    setLoader(1);
    const data = form.getState().values;
    FmcsaTransferApiFactory(undefined, axios?.defaults.baseURL, axios)
      .apiAppFmcsatransferGenerateeldfilePost({
        driverId: data.driverId,
        vehicleId: data.vehicleId,
        dateFrom: data.dateFrom,
        dateTo: data.dateTo,
        countryCode: "US",
      })
      .then((result) => {
        if (result.data.body) {
          let url = window.URL.createObjectURL(new Blob([result.data.body]));
          let a = document.createElement("a");
          a.href = url;
          a.download = (result.data.fileName ?? "eldFile") + ".csv";
          a.click();
          notify("resources.fmcsa-data-transfer.messages.fileGenerated");
        }
      })
      .catch((e) => {
        console.error(e);
        notify(handleAxiosError(e), { type: "error" });
      })
      .finally(() => setLoading(false));
  }, [form, axios, loading, notify]);

  return (
    <Toolbar {...props} style={{ gap: "10px" }}>
      <ToolbarCustomButton
        onClick={() => {
          submitToFmcsa();
        }}
        disabled={invalid}
        label="resources.fmcsa-data-transfer.labels.submitToFmcsa">
        {loading && loader === 0 ? (
          <CircularProgress
            size={16}
            thickness={2}
            className={classes.leftIcon}
          />
        ) : (
          <SaveIcon />
        )}
      </ToolbarCustomButton>
      <ToolbarCustomButton
        onClick={() => {
          downloadEldFile();
        }}
        disabled={invalid}
        label="resources.fmcsa-data-transfer.labels.generateEldFile">
        {loading && loader === 1 ? (
          <CircularProgress
            size={16}
            thickness={2}
            className={classes.leftIcon}
            color="secondary"
          />
        ) : (
          <GetAppIcon />
        )}
      </ToolbarCustomButton>
    </Toolbar>
  );
};

export const FmcsaDataTransferForm = (props: Props) => {
  useAuthenticated();
  useSearchFields(undefined, ["driverName"], resourceNames.ADMIN_DRIVERS);
  useSearchFields(undefined, ["name"], resourceNames.ADMIN_VEHICLES_VIEW);
  return (
    <SimpleForm
      className="form-create-edit"
      variant="outlined"
      redirect={false}
      toolbar={<FormToolbar />}
      {...props}>
      <RaDriverPicker
        source="driverId"
        label="resources.fmcsa-data-transfer.labels.driver"
        validate={required()}
      />
      <ReferenceInput
        reference={resourceNames.ADMIN_VEHICLES_VIEW}
        label="resources.fmcsa-data-transfer.labels.vehicle"
        source="vehicleId">
        <AutocompleteInput />
      </ReferenceInput>
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        source="dateFrom"
        label="resources.fmcsa-data-transfer.labels.dateFrom"
        validate={required()}
        options={{
          autoOk: true,
          format: "MMMM do yyyy",
        }}
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        source="dateTo"
        label="resources.fmcsa-data-transfer.labels.dateTo"
        validate={required()}
        options={{
          autoOk: true,
          format: "MMMM do yyyy",
        }}
      />
      <TextInput
        source="comment"
        validate={maxLength(60)}
        label="resources.fmcsa-data-transfer.labels.comment"
      />
    </SimpleForm>
  );
};
